import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Inbox,
  Trash2,
  Mail,
  ShieldCheck,
  RefreshCcw,
  MailOpen,
  LogOut,
  AlertTriangle,
  Activity,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api/admin/messages';
const HEALTH_API = '/api/health';

// The frontend makes sure to hit every endpoint with Cloudflare Access's
// session cookie intact: `credentials: 'include'` is implied same-origin,
// but we set it explicitly for readability.
const apiFetch = (path, init = {}) =>
  fetch(path, { credentials: 'same-origin', ...init });

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [listComplete, setListComplete] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);

  const loadHealth = useCallback(async () => {
    try {
      const res = await apiFetch(HEALTH_API);
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        setHealth({ ok: false, reachable: false });
        return;
      }
      const data = await res.json();
      setHealth({ ...data, reachable: true });
    } catch {
      setHealth({ ok: false, reachable: false });
    }
  }, []);

  const load = useCallback(async ({ reset = true } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ limit: '50' });
      if (!reset && cursor) qs.set('cursor', cursor);
      const res = await apiFetch(`${API}?${qs.toString()}`);

      if (res.status === 401 || res.status === 403) {
        setError('unauthorized');
        setMessages([]);
        return;
      }

      // Cloudflare Access returns an HTML login page for un-authed users.
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        setError('unauthorized');
        setMessages([]);
        return;
      }

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      setMessages((prev) => (reset ? data.messages : [...prev, ...data.messages]));
      setCursor(data.cursor);
      setListComplete(!!data.list_complete);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    load({ reset: true });
    loadHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (m, read) => {
    const res = await apiFetch(`${API}/${encodeURIComponent(m._key)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ read }),
    });
    if (!res.ok) {
      toast.error('Could not update message.');
      return;
    }
    setMessages((prev) => prev.map((x) => (x._key === m._key ? { ...x, read } : x)));
  };

  const deleteOne = async (m) => {
    if (!window.confirm(`Delete message from ${m.name}? This cannot be undone.`)) return;
    const res = await apiFetch(`${API}/${encodeURIComponent(m._key)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      toast.error('Could not delete message.');
      return;
    }
    setMessages((prev) => prev.filter((x) => x._key !== m._key));
    if (selectedKey === m._key) setSelectedKey(null);
    toast.success('Message deleted.');
  };

  const selected = messages.find((m) => m._key === selectedKey) || null;
  const unreadCount = messages.filter((m) => !m.read).length;

  // ---------- Unauthorized state ----------
  if (error === 'unauthorized') {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D95871]/15 mb-6">
          <AlertTriangle className="w-6 h-6 text-[#D95871]" />
        </div>
        <h1 className="text-3xl font-bold text-slate-50 mb-3">Access required</h1>
        <p className="text-slate-400 leading-relaxed max-w-md mx-auto">
          This area is protected by Cloudflare Access. You must be signed in
          with the admin email to view contact submissions.
        </p>
        <a
          href="/"
          className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-[#4A8FD1] hover:text-[#6AAFE0]"
        >
          Back to portfolio
        </a>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
                <ShieldCheck className="w-4 h-4" />
                <span className="w-8 h-px bg-[#4A8FD1]" />
                <span>Admin</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
                Contact Inbox
              </h1>
              <div className="mt-2 flex items-center gap-3 text-sm text-slate-400 flex-wrap">
                <span>{messages.length} total · {unreadCount} unread</span>
                {health && (
                  <button
                    type="button"
                    onClick={() => setHealthOpen((v) => !v)}
                    data-testid="admin-health-badge"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs mono uppercase tracking-wider border ${
                      health.ok
                        ? 'bg-[#4CB88A]/10 border-[#4CB88A]/30 text-[#7CDAB0]'
                        : 'bg-[#D95871]/10 border-[#D95871]/30 text-[#FFB3BF]'
                    }`}
                    title={health.ok ? 'All systems healthy' : 'Configuration issue detected'}
                  >
                    {health.ok ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5" />
                    )}
                    <span>{health.ok ? 'Healthy' : 'Issues'}</span>
                    {healthOpen ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/projects"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-white/15 text-slate-300 hover:bg-white/5"
                data-testid="admin-projects-link"
              >
                Projects
              </Link>
              <Button
                variant="outline"
                className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5"
                onClick={() => {
                  load({ reset: true });
                  loadHealth();
                }}
                disabled={loading}
                data-testid="admin-refresh-btn"
              >
                <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <a
                href="/cdn-cgi/access/logout"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-white/15 text-slate-300 hover:bg-white/5"
                data-testid="admin-logout-link"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Health drawer */}
      {healthOpen && health && (
        <div className="border-b border-white/10 bg-[#14192A]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-5">
            <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-slate-500 mb-3">
              <Activity className="w-4 h-4" />
              <span>System health</span>
              {health.generated_at && (
                <span className="ml-auto text-slate-600">
                  checked {formatDate(health.generated_at)}
                </span>
              )}
            </div>
            {!health.reachable ? (
              <div className="text-sm text-[#FFB3BF]">
                Health endpoint unreachable. In the preview environment this
                is expected; in production it means `/api/health` is blocked
                by Access or the Function is not deployed.
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(health.checks || []).map((c) => (
                  <li
                    key={c.name}
                    className={`flex items-start gap-3 px-3 py-2 rounded-md border text-sm ${
                      c.ok
                        ? 'border-white/10 bg-white/5 text-slate-300'
                        : 'border-[#D95871]/30 bg-[#D95871]/10 text-[#FFB3BF]'
                    }`}
                  >
                    {c.ok ? (
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#7CDAB0]" />
                    ) : (
                      <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#FFB3BF]" />
                    )}
                    <div className="min-w-0">
                      <div className="mono text-xs uppercase tracking-wider">
                        {c.name}
                      </div>
                      {c.detail && (
                        <div className="text-xs text-slate-400 break-all mt-0.5">
                          {typeof c.detail === 'object'
                            ? JSON.stringify(c.detail)
                            : c.detail}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <aside className="lg:col-span-5 space-y-2" data-testid="admin-messages-list">
          {loading && messages.length === 0 && (
            <div className="p-6 rounded-lg border border-white/10 bg-[#1A2231] text-slate-400 text-sm">
              Loading…
            </div>
          )}

          {!loading && messages.length === 0 && (
            <div className="p-8 rounded-lg border border-dashed border-white/15 bg-[#1A2231] text-center">
              <Inbox className="w-8 h-8 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">No messages yet</p>
              <p className="text-slate-500 text-sm mt-1">
                Submissions from the contact form will appear here.
              </p>
            </div>
          )}

          {messages.map((m) => {
            const isSelected = m._key === selectedKey;
            return (
              <button
                key={m._key}
                type="button"
                onClick={() => {
                  setSelectedKey(m._key);
                  if (!m.read) markRead(m, true);
                }}
                data-testid={`admin-msg-item-${m.id}`}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  isSelected
                    ? 'border-[#4A8FD1] bg-[#1F2A3D]'
                    : 'border-white/10 bg-[#1A2231] hover:border-white/25'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    {!m.read && (
                      <span
                        className="w-2 h-2 rounded-full bg-[#4A8FD1] flex-shrink-0"
                        aria-label="Unread"
                      />
                    )}
                    <span className="font-semibold text-slate-50 truncate">
                      {m.name}
                    </span>
                  </div>
                  <span className="text-[10px] mono uppercase tracking-wider text-slate-500 whitespace-nowrap">
                    {formatDate(m.created_at)}
                  </span>
                </div>
                <div className="text-sm text-slate-300 truncate mb-1">
                  {m.subject}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </button>
            );
          })}

          {!listComplete && (
            <Button
              variant="outline"
              className="w-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5"
              onClick={() => load({ reset: false })}
              disabled={loading}
            >
              Load more
            </Button>
          )}

          {error && error !== 'unauthorized' && (
            <div className="p-4 rounded-lg border border-[#D95871]/30 bg-[#D95871]/10 text-sm text-[#FFB3BF]">
              {error}
            </div>
          )}
        </aside>

        {/* Detail */}
        <section className="lg:col-span-7" data-testid="admin-message-detail">
          {!selected ? (
            <div className="p-10 rounded-lg border border-white/10 bg-[#1A2231] text-center">
              <Mail className="w-8 h-8 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-300">Select a message to view details.</p>
            </div>
          ) : (
            <article className="rounded-lg border border-white/10 bg-[#1A2231] overflow-hidden">
              <header className="p-5 sm:p-6 border-b border-white/10">
                <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-slate-500 mb-2">
                  <span>{formatDate(selected.created_at)}</span>
                  {selected.country && (
                    <>
                      <span>·</span>
                      <span>{selected.country}</span>
                    </>
                  )}
                  {selected.read ? (
                    <Badge variant="secondary" className="bg-white/10 text-slate-300">
                      Read
                    </Badge>
                  ) : (
                    <Badge className="bg-[#4A8FD1]/20 text-[#4A8FD1] border-[#4A8FD1]/30">
                      Unread
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-50 break-words">
                  {selected.subject}
                </h2>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <span className="text-slate-400">From</span>
                  <span className="text-slate-100 font-medium">{selected.name}</span>
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent('Re: ' + selected.subject)}`}
                    className="text-[#4A8FD1] hover:text-[#6AAFE0] break-all"
                  >
                    {selected.email}
                  </a>
                </div>
              </header>
              <div className="p-5 sm:p-6">
                <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </p>
              </div>
              <footer className="px-5 sm:px-6 py-4 border-t border-white/10 flex flex-wrap gap-2 justify-between">
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent('Re: ' + selected.subject)}`}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[#4A8FD1] text-slate-50 hover:bg-[#6AAFE0]"
                  >
                    <Mail className="w-4 h-4" />
                    Reply
                  </a>
                  <Button
                    variant="outline"
                    className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5"
                    onClick={() => markRead(selected, !selected.read)}
                  >
                    {selected.read ? (
                      <>
                        <Inbox className="w-4 h-4 mr-2" />
                        Mark unread
                      </>
                    ) : (
                      <>
                        <MailOpen className="w-4 h-4 mr-2" />
                        Mark read
                      </>
                    )}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  className="border-[#D95871]/30 bg-transparent text-[#FFB3BF] hover:bg-[#D95871]/10"
                  onClick={() => deleteOne(selected)}
                  data-testid="admin-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </footer>
            </article>
          )}
        </section>
      </div>
    </div>
  );
};

export default Admin;
